import { useState } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCookieConsent, CookiePreferences } from '@/hooks/useCookieConsent';

interface CookieConsentProps {
  onClose?: () => void;
  forceOpen?: boolean;
}

export const CookieConsent = ({ onClose, forceOpen = false }: CookieConsentProps) => {
  const { showBanner, acceptAll, rejectAll, saveCustomPreferences, preferences } = useCookieConsent();
  const [showCustomize, setShowCustomize] = useState(false);
  const [customPrefs, setCustomPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
  });

  const isVisible = forceOpen || showBanner;

  if (!isVisible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    onClose?.();
  };

  const handleRejectAll = () => {
    rejectAll();
    onClose?.();
  };

  const handleSaveCustom = () => {
    saveCustomPreferences(customPrefs);
    onClose?.();
  };

  const handleTogglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setCustomPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Version étendue (forceOpen depuis le footer)
  if (forceOpen) {
    return (
      <div
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      >
        <div className="bg-background border border-border/50 rounded-xl shadow-2xl p-5 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 id="cookie-consent-title" className="text-base font-semibold text-foreground">
              Gérer les cookies
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-muted transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <p id="cookie-consent-description" className="text-sm text-muted-foreground mb-4">
            Personnalisez vos préférences de cookies.{' '}
            <Link to="/politique-de-confidentialite#gestion-des-cookies" className="text-green-primary hover:underline">
              En savoir plus
            </Link>
          </p>

          {/* Options */}
          <div className="space-y-3 mb-5">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-foreground">Essentiels</p>
                <p className="text-xs text-muted-foreground">Requis</p>
              </div>
              <span className="text-xs text-green-primary font-medium">Actifs</span>
            </div>

            <div className="flex items-center justify-between py-2 border-t border-border/30">
              <div>
                <p className="text-sm font-medium text-foreground">Analytiques</p>
                <p className="text-xs text-muted-foreground">Améliorer le site</p>
              </div>
              <button
                onClick={() => handleTogglePreference('analytics')}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  customPrefs.analytics ? 'bg-green-primary' : 'bg-muted-foreground/30'
                }`}
                role="switch"
                aria-checked={customPrefs.analytics}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  customPrefs.analytics ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between py-2 border-t border-border/30">
              <div>
                <p className="text-sm font-medium text-foreground">Marketing</p>
                <p className="text-xs text-muted-foreground">Publicités</p>
              </div>
              <button
                onClick={() => handleTogglePreference('marketing')}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  customPrefs.marketing ? 'bg-green-primary' : 'bg-muted-foreground/30'
                }`}
                role="switch"
                aria-checked={customPrefs.marketing}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  customPrefs.marketing ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleRejectAll} variant="outline" size="sm" className="flex-1">
              Refuser
            </Button>
            <Button onClick={handleSaveCustom} size="sm" className="flex-1 bg-green-primary hover:bg-green-primary/90 text-white">
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Version minimaliste (bannière initiale)
  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed bottom-4 left-4 right-4 md:right-auto md:max-w-sm z-50 animate-fade-in"
    >
      <div className="bg-background/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-lg p-3">
        <p id="cookie-consent-description" className="text-xs text-muted-foreground mb-3">
          <span id="cookie-consent-title" className="sr-only">Cookies</span>
          Ce site utilise des cookies.{' '}
          <Link to="/politique-de-confidentialite#gestion-des-cookies" className="text-green-primary hover:underline">
            En savoir plus
          </Link>
        </p>

        <div className="flex gap-2">
          <Button onClick={handleRejectAll} variant="outline" size="sm" className="flex-1 text-xs h-8">
            Refuser
          </Button>
          <Button onClick={handleAcceptAll} size="sm" className="flex-1 text-xs h-8 bg-green-primary hover:bg-green-primary/90 text-white">
            Accepter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
